import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Filter, ArrowUpRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";

// Curated İKEL MT quick-order catalog.
// Prices are indicative retail references; quotes confirmed on request.
const PRODUCTS = [
  { sku: "IKM-H35-SC-CR", type: "hinge",   mech: "softClose",   finish: "chrome",     loadKg: 10, qtyPerPack: 50, minOrder: 100 },
  { sku: "IKM-H35-SC-GD", type: "hinge",   mech: "softClose",   finish: "gold",       loadKg: 10, qtyPerPack: 50, minOrder: 100 },
  { sku: "IKM-H35-SC-MB", type: "hinge",   mech: "softClose",   finish: "matteBlack", loadKg: 10, qtyPerPack: 50, minOrder: 100 },
  { sku: "IKM-H2W-HD-CR", type: "hinge",   mech: "twoWay",      finish: "chrome",     loadKg: 25, qtyPerPack: 30, minOrder: 60 },
  { sku: "IKM-H2W-HD-MB", type: "hinge",   mech: "twoWay",      finish: "matteBlack", loadKg: 25, qtyPerPack: 30, minOrder: 60 },
  { sku: "IKM-SL450-SC",  type: "slide",   mech: "softClose",   finish: "chrome",     loadKg: 45, qtyPerPack: 10, minOrder: 20 },
  { sku: "IKM-SL500-BO",  type: "slide",   mech: "bounceOff",   finish: "chrome",     loadKg: 50, qtyPerPack: 10, minOrder: 20 },
  { sku: "IKM-BL-GAS-70", type: "bedLift", mech: "gasAssisted", finish: "chrome",     loadKg: 70, qtyPerPack: 2,  minOrder: 10 },
  { sku: "IKM-FD-LIFT-S", type: "flap",    mech: "softClose",   finish: "chrome",     loadKg: 8,  qtyPerPack: 20, minOrder: 40 },
  { sku: "IKM-FD-FOLD-M", type: "flap",    mech: "fold",        finish: "matteBlack", loadKg: 12, qtyPerPack: 20, minOrder: 40 },
  { sku: "IKM-LEG-60-MB", type: "leg",     mech: "fixed",       finish: "matteBlack", loadKg: 60, qtyPerPack: 10, minOrder: 20 },
  { sku: "IKM-LEG-80-GD", type: "leg",     mech: "fixed",       finish: "gold",       loadKg: 60, qtyPerPack: 10, minOrder: 20 },
  { sku: "IKM-LEG-120-CR",type: "leg",     mech: "fixed",       finish: "chrome",     loadKg: 80, qtyPerPack: 10, minOrder: 20 },
];

export default function QuickOrderTable() {
  const { t } = useLang();
  const q = t.ikelPage.quickOrder;

  const [finish, setFinish] = useState("all");
  const [mech, setMech] = useState("all");
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return PRODUCTS.filter((p) => {
      if (finish !== "all" && p.finish !== finish) return false;
      if (mech !== "all" && p.mech !== mech) return false;
      if (s && !p.sku.toLowerCase().includes(s) && !q.types[p.type].toLowerCase().includes(s)) return false;
      return true;
    });
  }, [finish, mech, search, q.types]);

  return (
    <section
      className="py-24 md:py-32 bg-[#F5F5F7]"
      data-testid="quick-order-section"
    >
      <div className="pgb-container">
        <div className="max-w-3xl mb-12">
          <span className="eyebrow text-[#1D4ED8]">{q.eyebrow}</span>
          <h2 className="mt-4 text-3xl md:text-5xl font-black tracking-tighter text-[#0B1B29]">
            {q.title}
          </h2>
          <p className="mt-5 text-[#536878] leading-relaxed max-w-2xl">
            {q.subtitle}
          </p>
        </div>

        {/* Filters */}
        <div
          className="bg-white border border-[#E2E8F0] p-5 md:p-6 mb-0 flex flex-wrap gap-4 md:gap-6 items-end"
          data-testid="quick-order-filters"
        >
          <div className="flex-1 min-w-[220px]">
            <label
              htmlFor="qo-search"
              className="flex items-center gap-2 text-[10px] font-bold tracking-[0.22em] uppercase text-[#536878] mb-2"
            >
              <Search size={12} /> {q.filters.search}
            </label>
            <input
              id="qo-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={q.filters.searchPlaceholder}
              className="qo-input"
              data-testid="qo-search"
            />
          </div>

          <div className="min-w-[180px]">
            <label
              htmlFor="qo-finish"
              className="flex items-center gap-2 text-[10px] font-bold tracking-[0.22em] uppercase text-[#536878] mb-2"
            >
              <Filter size={12} /> {q.filters.finish}
            </label>
            <select
              id="qo-finish"
              value={finish}
              onChange={(e) => setFinish(e.target.value)}
              className="qo-input"
              data-testid="qo-finish"
            >
              <option value="all">{q.filters.all}</option>
              {Object.entries(q.finishes).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>

          <div className="min-w-[180px]">
            <label
              htmlFor="qo-mech"
              className="flex items-center gap-2 text-[10px] font-bold tracking-[0.22em] uppercase text-[#536878] mb-2"
            >
              <Filter size={12} /> {q.filters.mech}
            </label>
            <select
              id="qo-mech"
              value={mech}
              onChange={(e) => setMech(e.target.value)}
              className="qo-input"
              data-testid="qo-mech"
            >
              <option value="all">{q.filters.all}</option>
              {Object.entries(q.mechanisms).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>

          <div
            className="text-[11px] font-mono tracking-[0.18em] uppercase text-[#536878] pb-3"
            data-testid="qo-result-count"
          >
            {filtered.length} {q.results}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#E2E8F0] border-t-0 overflow-hidden">
          <div className="hidden md:grid grid-cols-12 px-6 py-3 text-[10px] font-bold tracking-[0.2em] uppercase text-[#536878] bg-[#F5F5F7] border-b border-[#E2E8F0]">
            <div className="col-span-3">{q.headers.sku}</div>
            <div className="col-span-2">{q.headers.type}</div>
            <div className="col-span-2">{q.headers.mech}</div>
            <div className="col-span-2">{q.headers.finish}</div>
            <div className="col-span-1 text-right">{q.headers.load}</div>
            <div className="col-span-2 text-right">{q.headers.action}</div>
          </div>

          {filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#536878]" data-testid="qo-empty">
              {q.noResults}
            </div>
          ) : (
            filtered.map((p, idx) => (
              <div
                key={p.sku}
                className="grid grid-cols-2 md:grid-cols-12 gap-y-2 px-6 py-5 items-center border-b border-[#E2E8F0] last:border-b-0 hover:bg-[#F5F5F7] transition-colors"
                data-testid={`qo-row-${idx}`}
              >
                <div className="col-span-2 md:col-span-3 font-mono text-xs md:text-sm tracking-wider text-[#0B1B29] font-bold">
                  {p.sku}
                </div>
                <div className="col-span-1 md:col-span-2 text-sm text-[#0B1B29]">
                  {q.types[p.type]}
                </div>
                <div className="col-span-1 md:col-span-2 text-sm text-[#536878]">
                  {q.mechanisms[p.mech]}
                </div>
                <div className="col-span-1 md:col-span-2 flex items-center gap-2">
                  <span
                    className={`w-3 h-3 rounded-full border border-black/10 ${finishDot(p.finish)}`}
                    aria-hidden="true"
                  />
                  <span className="text-sm text-[#0B1B29]">{q.finishes[p.finish]}</span>
                </div>
                <div className="col-span-1 md:col-span-1 md:text-right">
                  <span className="font-mono text-xs text-[#536878]">≤ {p.loadKg} kg</span>
                </div>
                <div className="col-span-2 md:col-span-2 md:text-right">
                  <Link
                    to={`/quote?from=quick-order&sku=${encodeURIComponent(p.sku)}`}
                    className="inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.15em] uppercase text-[#1D4ED8] hover:text-[#0B1B29]"
                    data-testid={`qo-quote-${idx}`}
                  >
                    {q.quoteCta} <ArrowUpRight size={14} />
                  </Link>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <style>{`
        .qo-input {
          width: 100%;
          padding: 10px 12px;
          background: #ffffff;
          border: 1px solid #E2E8F0;
          font-size: 13px;
          color: #0B1B29;
          outline: none;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .qo-input:focus {
          border-color: #1D4ED8;
          box-shadow: 0 0 0 3px rgba(29,78,216,0.15);
        }
      `}</style>
    </section>
  );
}

function finishDot(finish) {
  switch (finish) {
    case "chrome":     return "bg-gradient-to-br from-[#E5E7EB] to-[#9CA3AF]";
    case "gold":       return "bg-gradient-to-br from-[#FCD34D] to-[#D97706]";
    case "matteBlack": return "bg-[#0B1B29]";
    default:           return "bg-[#536878]";
  }
}
